import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { fmtDate } from '@/lib/utils'
import { PageHeader } from '@/components/layout/PageHeader'
import { Badge } from '@/components/ui/Badge'
import { QueryError } from '@/components/ui/QueryError'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
  EmptyState,
  LoadingRows,
} from '@/components/ui/DataTable'

interface AdminChannel {
  id: number
  name: string
  type?: string
  zoneId?: number
  zoneName?: string
  createdAt?: string
}

export function ChannelsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-channels'],
    queryFn: () => api<{ channels: AdminChannel[] }>('/api/admin/channels?limit=100'),
  })

  const channels = data?.channels || []

  return (
    <div>
      <PageHeader
        title="Channels"
        description="Platform-wide channel directory. Create, rename, and delete channels in zone settings within chat."
      />

      {error ? (
        <QueryError title="Couldn’t load channels" error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable>
          <table className="w-full min-w-[560px]">
            <DataTableHead>
              <DataTableHeaderCell>Channel</DataTableHeaderCell>
              <DataTableHeaderCell>Zone</DataTableHeaderCell>
              <DataTableHeaderCell>Type</DataTableHeaderCell>
              <DataTableHeaderCell>Created</DataTableHeaderCell>
            </DataTableHead>
            {isLoading ? (
              <LoadingRows cols={4} />
            ) : channels.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={4}>
                    <EmptyState title="No channels" />
                  </td>
                </tr>
              </tbody>
            ) : (
              <DataTableBody>
                {channels.map((c) => (
                  <DataTableRow key={c.id}>
                    <DataTableCell>
                      <span className="font-semibold">#{c.name}</span>
                      <span className="text-xs text-[#93a5ad] ml-2">id {c.id}</span>
                    </DataTableCell>
                    <DataTableCell>
                      {c.zoneName ? (
                        <span>{c.zoneName} <span className="text-[#93a5ad]">#{c.zoneId}</span></span>
                      ) : (
                        '—'
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      <Badge variant="default">{c.type || 'text'}</Badge>
                    </DataTableCell>
                    <DataTableCell className="text-[#9aabbb] text-xs">
                      {fmtDate(c.createdAt)}
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            )}
          </table>
        </DataTable>
      )}
    </div>
  )
}
